import MODULE_NAMES from 'App/config/moduleNames';
import { USER_ROLES } from 'App/domain/user';
import { FEATURE_TOGGLES } from 'sharedComponents/FeatureToggle';
import ReportingIcon from '@mui/icons-material/Assessment';
import ResourceIcon from '@mui/icons-material/Description';
import ConnectivityIcon from '@mui/icons-material/Lan';
import BeatboxIcon from '@mui/icons-material/MonitorHeart';
import AlertsIcon from '@mui/icons-material/Notifications';
import FlowTracesIcon from '@mui/icons-material/Reorder';
import SecurityIcon from '@mui/icons-material/Security';
import { FOUNDATION } from 'config/features';
import FlowsIcon from 'connect-icons/Flow';
import HeartbeatIcon from 'connect-icons/Heartbeat';
import OverviewIcon from 'connect-icons/Overview';
import qs from 'qs';
import { useMemo } from 'react';
import { defineMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { FILTERS } from './filters';

export const MODULE_PATH = FOUNDATION ? 'connect' : 'heartbeat';

export const OVERVIEW = '';
export const FLOWS = 'flows';
export const FLOW_TRACES = 'flow-traces';
export const FLOW = 'flows/:id/*';
export const FLOW_ACCESS = 'flow-access';
export const SERVICE_ACCOUNTS = 'service-accounts';
export const RESOURCES_BASE = 'resources';
export const RESOURCES = `${RESOURCES_BASE}/*`;
export const DYNAMIC_TABLE = `${RESOURCES_BASE}/dynamic-table/:tableName`;
export const RESOURCE = `${RESOURCES_BASE}/:type/:id/:revision`;
export const ALERTS = 'alerts';
export const REPORTING = 'reporting';
export const BEATBOX = 'beatbox';
export const BEATBOX_FLOW = 'flows/beatbox/:beatboxId/:id/*';
export const AGENT_FLOW = 'flows/agent/:agentId/:id/*';
export const SETTINGS = 'settings';
export const CONNECTIVITY_CHECK = 'connectivity';
export const AGENT = 'agent';
export const CERTIFICATES = 'certificates';
export const SECURITY = 'security/*';

const paramFields = ['fromDate', 'toDate', 'dateOptionIndex', 'offset'];

const getParams = search => {
  const cleaned = search.replace('?', '');
  const params = qs.parse(cleaned);
  const newParams = {};
  paramFields.forEach(fieldKey => {
    if (params[fieldKey]) {
      newParams[fieldKey] = params[fieldKey];
    }
  });
  const stringified = qs.stringify(newParams);
  return stringified ? `?${stringified}` : '';
};

const children = [
  {
    index: true,
    path: OVERVIEW,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Overview" */ '../scenes/Overview'),
    intlMessage: defineMessage({ id: 'route.heartbeat.overview', defaultMessage: 'Overview' }),
    Icon: OverviewIcon,
  },
  {
    path: FLOWS,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Flows" */ '../scenes/Flows'),
    intlMessage: defineMessage({ id: 'route.heartbeat.flows', defaultMessage: 'Flows' }),
    Icon: FlowsIcon,
  },
  {
    path: FLOW,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Flow" */ '../scenes/Flow'),
  },
  {
    path: FLOW_TRACES,
    lazy: () => import(/* webpackChunkName: "Heartbeat:FlowTraces" */ '../scenes/FlowTraces'),
    intlMessage: defineMessage({ id: 'route.heartbeat.flowTraces', defaultMessage: 'Flow traces' }),
    Icon: FlowTracesIcon,
  },
  {
    path: RESOURCES,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Resources" */ '../scenes/Resources'),
    intlMessage: defineMessage({ id: 'route.heartbeat.resources', defaultMessage: 'Resources' }),
    Icon: ResourceIcon,
  },
  {
    path: RESOURCE,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Resource" */ '../scenes/Resource'),
  },
  {
    path: DYNAMIC_TABLE,
    lazy: () => import(/* webpackChunkName: "Heartbeat:ResourceDynamicTable" */ '../scenes/ResourceDynamicTable'),
  },
  {
    path: ALERTS,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Alerts" */ '../scenes/Alerts'),
    intlMessage: defineMessage({ id: 'route.heartbeat.alerts', defaultMessage: 'Alerts' }),
    Icon: AlertsIcon,
    featureToggle: FEATURE_TOGGLES.ALERTS,
    restrictRoleAccess: USER_ROLES.DEPLOYER,
  },
  {
    path: REPORTING,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Reporting" */ '../scenes/Reporting'),
    intlMessage: defineMessage({ id: 'route.heartbeat.reporting', defaultMessage: 'Reporting' }),
    Icon: ReportingIcon,
    featureToggle: FEATURE_TOGGLES.REPORTING,
    params: `?filterBars=${JSON.stringify({ [FILTERS.HB_REPORTING_FILTER]: { filters: ['FLOW:Show all flows'] } })}`,
  },
  {
    path: BEATBOX,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Beatbox" */ '../scenes/Beatbox'),
    intlMessage: defineMessage({ id: 'route.heartbeat.beatbox', defaultMessage: 'Beatbox' }),
    Icon: BeatboxIcon,
    featureToggle: FEATURE_TOGGLES.BEATBOX,
    restrictRoleAccess: USER_ROLES.DEPLOYER,
  },
  {
    path: BEATBOX_FLOW,
    lazy: () => import(/* webpackChunkName: "Heartbeat:BeatboxFlow" */ '../scenes/BeatboxFlow'),
  },
  {
    path: AGENT,
    featureToggle: FEATURE_TOGGLES.AGENT,
    intlMessage: defineMessage({ id: 'route.heartbeat.agent', defaultMessage: 'Agents' }),
    Icon: BeatboxIcon,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Agents" */ '../scenes/Agents'),
  },
  {
    path: AGENT_FLOW,
    lazy: () => import(/* webpackChunkName: "Heartbeat:AgentFlow" */ '../scenes/AgentFlow'),
  },
  {
    path: SETTINGS,
    lazy: () => import(/* webpackChunkName: "Heartbeat:Settings" */ 'Settings/scenes/UserConfiguration'),
  },
  {
    path: CONNECTIVITY_CHECK,
    lazy: () => import(/* webpackChunkName: "Heartbeat:ConnectivityCheck" */ 'Settings/scenes/ConnectivityCheck'),
    intlMessage: defineMessage({ id: 'route.heartbeat.connectivityCheck', defaultMessage: 'Connectivity check' }),
    Icon: ConnectivityIcon,
    featureToggle: FEATURE_TOGGLES.CONNECTIVITY_CHECK,
    restrictRoleAccess: USER_ROLES.DEPLOYER,
  },
  {
    path: SECURITY,
    lazy: () => import(/* webpackChunkName: "Connect:Security" */ '../scenes/Security'),
    intlMessage: defineMessage({ id: 'route.heartbeat.security', defaultMessage: 'Security' }),
    Icon: SecurityIcon,
  },
];

const heartbeat = {
  path: MODULE_PATH,
  lazy: () => import(/* webpackChunkName: "Heartbeat" */ 'Heartbeat'),
  intlMessage: FOUNDATION
    ? defineMessage({ id: 'route.connect', defaultMessage: 'Connect' })
    : defineMessage({ id: 'route.heartbeat', defaultMessage: 'Heartbeat' }),
  Icon: <HeartbeatIcon />,
  moduleNameForOrganisationAccess: MODULE_NAMES.HEARTBEAT,
  children,
};

export const useRoutes = activeRole => {
  const location = useLocation();
  return useMemo(
    () => ({
      intlMessage: heartbeat.intlMessage,
      Icon: heartbeat.Icon,
      root: heartbeat.path,
      moduleNameForOrganisationAccess: heartbeat.moduleNameForOrganisationAccess,
      routes: heartbeat.children
        .filter(child => !!child.intlMessage)
        .map(({ path, Icon, featureToggle, restrictRoleAccess, intlMessage, index }) => ({
          index,
          path: path.replace(':organizationId', activeRole?.organizationId),
          intlMessage,
          Icon,
          featureToggle,
          restrictRoleAccess,
          params: getParams(location.search),
        })),
    }),
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeRole doesn't always change
    [location.search, activeRole]
  );
};

export default heartbeat;
